/*
 * Every URL of the storefront. Paths are kept identical to the current site so links, bookmarks,
 * e-mails and search-engine results keep working.
 */
import { ALL_CATEGORIES, type VehicleKind, type VehicleSearch } from '@/services/domain';

export const paths = {
  home: '/',
  results: '/listings/listing-grid',
  vehicle: (id: string) => `/listings/listing-details/${encodeURIComponent(id)}`,
  checkout: (vehicleId: string) => `/pages/booking-detail/${encodeURIComponent(vehicleId)}`,
  confirmation: (bookingId: string) => `/pages/booking-success?id=${encodeURIComponent(bookingId)}`,
  trips: '/user/reservationsHistory',
  favourites: '/user/user-wishlist',
  profile: '/user/user-profile/profile',
  messages: '/user/user-messages',
  reviews: '/user/user-reviews',
  login: '/authentication/login',
  register: '/authentication/register',
  forgotPassword: '/authentication/forgot-password',
  resetPassword: '/authentication/reset-password',
  partner: '/join-us',
  experiences: '/activities',
  experience: (id: string) => `/activities/details/${encodeURIComponent(id)}`,
  experienceCheckout: (id: string) => `/activities/checkout/${encodeURIComponent(id)}`,
  experienceBooked: (bookingId: string) => `/activities/booking-success/${encodeURIComponent(bookingId)}`,
  about: '/pages/aboutUs',
  faq: '/pages/faq',
  terms: '/pages/terms-condition',
  privacy: '/pages/privacy-policy',
  contact: '/contact-us',
  blog: '/blog',
  blogPost: (id: string) => `/blog/blog-details/${encodeURIComponent(id)}`,
  agency: (slug: string) => `/pages/agency/${encodeURIComponent(slug)}`,
  /* New URLs (not in the legacy site): city landing pages for search engines (Decision 110). */
  carCity: (slug: string) => `/location-voiture/${encodeURIComponent(slug)}`,
  experienceCity: (slug: string) => `/activites/${encodeURIComponent(slug)}`,
  /* Their English versions (Decision 112): the only English URLs of the site. */
  carCityEn: (slug: string) => `/en/car-rental/${encodeURIComponent(slug)}`,
  experienceCityEn: (slug: string) => `/en/experiences/${encodeURIComponent(slug)}`,
} as const;

/** «Reprendre» an unpaid cart: the checkout resumes the payment of that reservation (booking feature). */
export const resumeCartUrl = (vehicleId: string, reservationId: string) =>
  `${paths.checkout(vehicleId)}?${new URLSearchParams({ resume: reservationId })}`;

/** Adds `?redirect=` so sign-in returns the user where they were. */
export const withRedirect = (path: string, redirect: string) => `${path}?redirect=${encodeURIComponent(redirect)}`;

/** The sign-in, sign-up and password pages (the old site's `isOnAuthPage`). */
export const isAuthPath = (pathname: string) =>
  [paths.login, paths.register, paths.forgotPassword, paths.resetPassword].some((p) => pathname.startsWith(p));

/**
 * AU-02 / CK-02: the sign-in page after an expired session, `?session=expired&redirect=<path+query>` (the old
 * site's `redirectToLogin`); no `redirect` when `from` is itself an auth page.
 */
export function sessionExpiredUrl(from: string): string {
  const p = new URLSearchParams({ session: 'expired' });
  if (from && !isAuthPath(from)) p.set('redirect', from);
  return `${paths.login}?${p}`;
}

/* ── Vehicle search in the URL ── */
const KEYS = {
  city: 'city',
  kind: 'type',
  start: 'startDate',
  end: 'returnDate',
  pickupTime: 'pickupTime',
  returnTime: 'returnTime',
  delivery: 'delivery',
} as const;

export function searchToParams(s: VehicleSearch, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams({
    [KEYS.city]: s.city,
    [KEYS.kind]: s.kind,
    [KEYS.start]: s.start,
    [KEYS.end]: s.end,
    [KEYS.pickupTime]: s.pickupTime,
    [KEYS.returnTime]: s.returnTime,
    ...(s.delivery ? { [KEYS.delivery]: '1' } : {}),
    ...extra,
  });
  return p.toString();
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
/** `type` values of the old site's URLs. */
const LEGACY_KIND: Record<string, VehicleKind> = { bikes: 'moto', bike: 'moto', motos: 'moto', cars: 'car' };
const HM = /^\d{2}:\d{2}$/;

/** Reads a search from URL params. Returns only the valid fields. */
export function paramsToSearch(params: URLSearchParams): Partial<VehicleSearch> {
  const out: Partial<VehicleSearch> = {};
  const city = params.get(KEYS.city);
  const kind = params.get(KEYS.kind);
  const start = params.get(KEYS.start);
  const end = params.get(KEYS.end);
  const pt = params.get(KEYS.pickupTime);
  const rt = params.get(KEYS.returnTime);
  if (city) out.city = city;
  if (kind === 'car' || kind === 'moto') out.kind = kind as VehicleKind;
  // Old links (P15): `type=bikes` / `type=cars` of the old grid.
  else if (kind && LEGACY_KIND[kind.toLowerCase()]) out.kind = LEGACY_KIND[kind.toLowerCase()];
  // The old vehicle page also read `pickup` / `return` (P15).
  const startDay = start ?? params.get('pickup')?.slice(0, 10);
  const endDay = end ?? params.get('return')?.slice(0, 10);
  if (startDay && ISO.test(startDay)) out.start = startDay;
  if (endDay && ISO.test(endDay)) out.end = endDay;
  if (pt && HM.test(pt)) out.pickupTime = pt;
  if (rt && HM.test(rt)) out.returnTime = rt;
  if (params.get(KEYS.delivery) === '1') out.delivery = true;
  return out;
}

export const resultsUrl = (s: VehicleSearch, category?: string) =>
  `${paths.results}?${searchToParams(s, category && category !== ALL_CATEGORIES ? { category } : {})}`;
export const vehicleUrl = (id: string, s: VehicleSearch) => `${paths.vehicle(id)}?${searchToParams(s)}`;
